// ============================================================================
// BUSINESS UNIT (BRANCH) HOOKS
// ============================================================================

import { useState, useEffect, useCallback } from 'react';
import { businessUnitService } from 'api/services/businessUnitService';
import type { BusinessUnit, CreateBusinessUnitRequest, UpdateBusinessUnitRequest, BusinessUnitFilterParams } from 'api/types/businessUnit.types';
import { notifyDataChanged, useDataChangeListener } from './useDataSync';

const DATA_KEY = 'business-units';

export const useBusinessUnits = (params?: BusinessUnitFilterParams) => {
  const [data, setData] = useState<BusinessUnit[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchBusinessUnits = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await businessUnitService.getAll({ pageSize: 50000, ...params });
      setData(response.data);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    fetchBusinessUnits();
  }, [fetchBusinessUnits]);

  useDataChangeListener(DATA_KEY, fetchBusinessUnits);

  return { data, loading, error, refetch: fetchBusinessUnits };
};

export const useCreateBusinessUnit = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const createBusinessUnit = useCallback(async (payload: CreateBusinessUnitRequest) => {
    setLoading(true);
    setError(null);
    try {
      const response = await businessUnitService.create(payload);
      notifyDataChanged(DATA_KEY);
      return response.data;
    } catch (err) {
      setError(err as Error);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { createBusinessUnit, loading, error };
};

export const useUpdateBusinessUnit = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const updateBusinessUnit = useCallback(async (id: string, payload: UpdateBusinessUnitRequest) => {
    setLoading(true);
    setError(null);
    try {
      const response = await businessUnitService.update(id, payload);
      notifyDataChanged(DATA_KEY);
      return response.data;
    } catch (err) {
      setError(err as Error);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { updateBusinessUnit, loading, error };
};

export const useDeleteBusinessUnit = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const deleteBusinessUnit = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      await businessUnitService.delete(id);
      notifyDataChanged(DATA_KEY);
    } catch (err) {
      setError(err as Error);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { deleteBusinessUnit, loading, error };
};
